/**
 * Silent session refresh — refresh cookie + mint new context token (same as manual "stay on page" flow).
 * Used by proactive expiry hook, axios 401 coordinator, and activity-based triggers.
 */

import { store } from '@/app/store'
import { authService } from '@/services/api/shared/auth.service'
import { tokenManager } from '@/utils/tokenManager'
import { setContextToken } from '@/store/slices/auth.slice'
import { emitAuthBroadcastIfSupported } from '@/lib/session/auth-broadcast'

export type SilentRefreshOptions = {
  /** When provided (AuthenticatedLayout only), applies UI prefs from select-context response */
  applyUIPreferences?: (prefs: unknown | null | undefined) => void
}

let debouncedRefreshTimer: ReturnType<typeof setTimeout> | null = null
const DEBOUNCE_MS = 800

export function scheduleDebouncedSilentRefresh(delayMs = DEBOUNCE_MS) {
  if (debouncedRefreshTimer) clearTimeout(debouncedRefreshTimer)
  debouncedRefreshTimer = setTimeout(() => {
    debouncedRefreshTimer = null
    void performFullSilentRefresh().catch(() => {
      /* errors surfaced via callers / 401 */
    })
  }, delayMs)
}

/**
 * Select context from current Redux workspace (no access-token refresh).
 */
export async function performSilentReselectOnly(options?: SilentRefreshOptions): Promise<{
  success: boolean
  contextToken?: string
}> {
  const auth = store.getState().auth
  const currentContext = auth.currentContext
  const contextData = auth.contextData

  const contextTypeRaw = currentContext?.type || contextData?.contextType?.toUpperCase()
  if (!contextTypeRaw) {
    return { success: false }
  }

  try {
    sessionStorage.setItem('skipNextWorkspaceBootstrap', 'true')

    const organizationSlug = currentContext?.organizationSlug || contextData?.organizationSlug
    const branchSlug = currentContext?.branchSlug || contextData?.branchSlug
    const hasBranch = !!branchSlug || !!contextData?.branchId

    const result = await authService.selectContext({
      contextType: contextTypeRaw.toLowerCase() as 'platform' | 'organization' | 'branch',
      organizationSlug,
      branchSlug: hasBranch ? branchSlug : undefined,
    })

    if ('requiresStudentSelection' in result && result.requiresStudentSelection) {
      return { success: false }
    }

    if (!('contextToken' in result) || !result.contextToken) {
      return { success: false }
    }

    if ('uiPreferences' in result && result.uiPreferences != null && options?.applyUIPreferences) {
      options.applyUIPreferences(result.uiPreferences)
    }

    tokenManager.setToken('contextToken', result.contextToken, { expires: 7 })
    store.dispatch(setContextToken(result.contextToken))
    emitAuthBroadcastIfSupported({ type: 'context-token-updated', contextToken: result.contextToken })

    return { success: true, contextToken: result.contextToken }
  } catch {
    return { success: false }
  }
}

/**
 * Refresh access token (cookie) then select-context.
 */
export async function performFullSilentRefresh(options?: SilentRefreshOptions): Promise<{
  success: boolean
  contextToken?: string
}> {
  try {
    await authService.refreshToken()
  } catch {
    return { success: false }
  }
  return performSilentReselectOnly(options)
}
